import { useCallback, useRef } from 'react';

/**
 * Root-level attribute naming the period column(s) whose boundary is hovered right now — a single
 * field, or two space-separated fields for a dash-merged header label, whose one text names both
 * dates of its pair (a cross-month merged label instead targets each date line to its own single
 * field — see AxisLabel.pairPerLine).
 *
 * EITHER end of a boundary raises the mark: the header date (AxisBoundaryHeader) and the gridline
 * itself (AxisGridlineHoverTarget). They live in unrelated DOM subtrees — MUI's header row vs. the
 * virtualized rows — so a plain :hover can never reach from one to the other. Marking the shared
 * grid ROOT instead, and letting GeneratikTable.styles.tsx's per-index rules carry it back to both
 * ends, keeps the emphasis pure CSS: rows that virtualize in while hovered pick it up on mount,
 * and MUI's own className rewrites (which wipe imperatively added CELL classes — see the drag
 * machinery) can't touch a root attribute React never manages.
 */
export const AXIS_HOVER_ATTR = 'data-gt-axis-hover';

/**
 * Names the boundary an element speaks for, so a root mark raised at the gridline end can find the
 * header date back up: the gridline is addressable by its own `data-field`, a date is not — it may
 * live in a neighbour's header cell (a merged label hosts its pair) or be one of two stacked lines.
 */
export const AXIS_FIELD_ATTR = 'data-gt-axis-field';

/** Spread onto an element to declare which boundary its emphasis follows; a no-op without one. */
export const axisFieldAttr = (mark?: string) => (mark ? { [AXIS_FIELD_ATTR]: mark } : {});

/**
 * Raising and lowering the root hover mark, shared by both ends of a boundary.
 */
export const useAxisHoverMark = () => {
  const gridRootRef = useRef<HTMLElement | null>(null);
  // What THIS component last stamped — the guard for every clear below: mouseleave/mouseenter
  // order between two hover targets, or a stale clear after an axis rebuild, must never wipe a
  // mark some other boundary (or this label's other line) has since set.
  const activeMarkRef = useRef<string | null>(null);

  const clearHoverMark = useCallback(() => {
    const root = gridRootRef.current;
    const mark = activeMarkRef.current;
    gridRootRef.current = null;
    activeMarkRef.current = null;
    if (mark != null && root?.getAttribute(AXIS_HOVER_ATTR) === mark) {
      root.removeAttribute(AXIS_HOVER_ATTR);
    }
  }, []);

  const markHover = useCallback(
    (mark: string) => (event: React.MouseEvent<HTMLElement>) => {
      const root = event.currentTarget.closest<HTMLElement>('.MuiDataGrid-root');
      if (!root) return;
      gridRootRef.current = root;
      activeMarkRef.current = mark;
      root.setAttribute(AXIS_HOVER_ATTR, mark);
    },
    [],
  );

  // A hover target removed from the DOM mid-hover (axis rebuild, a row virtualizing out, the
  // resolver dropping this date) fires no mouseleave anywhere, so the ref's detach is the only
  // signal left to clear the mark on. The identity must stay stable across re-renders — a fresh
  // callback each render would make React detach/re-attach, clearing the mark on every unrelated
  // re-render while still hovered — which clearHoverMark's empty dependency list provides (its
  // state lives in the two refs above).
  const hoverRef = useCallback(
    (node: HTMLElement | null) => {
      if (node == null) clearHoverMark();
    },
    [clearHoverMark],
  );

  return { markHover, clearHoverMark, hoverRef };
};
